<template>
  <div
    class="delivery-page"
    v-loading="pageLoading"
    element-loading-text="数据处理中......"
  >
    <div class="page-header">
      <div class="page-header__left">
        <h2 class="page-header__title">{{ pageTitle }}</h2>
      </div>
      <div class="page-header__actions">
        <el-button size="small" plain @click="backHome">返回</el-button>
        <el-button
          v-if="showDraftButton"
          size="small"
          plain
          :loading="draftLoading"
          @click="handleSaveDraft"
        >暂存草稿</el-button>
        <el-button
          type="primary"
          size="small"
          :loading="submitLoading"
          @click="handleSubmit"
        >{{ submitButtonLabel }}</el-button>
        <el-button
          v-if="showAuditButton"
          type="success"
          size="small"
          plain
          :loading="auditLoading"
          @click="handleAudit(1)"
        >审核</el-button>
      </div>
    </div>

    <div class="delivery-card">
      <div class="delivery-card__head">
        <div class="delivery-card__head-left">
          <span class="delivery-card__label">出货单</span>
          <span class="delivery-card__title">{{ cardTitle }}</span>
        </div>
      </div>
      <el-form
        :model="form"
        label-position="top"
        size="small"
        class="delivery-form"
      >
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="委外订单号" required>
              <el-select
                v-model="form.baseOutsourcingDocId"
                filterable
                clearable
                placeholder="请选择委外订单"
                style="width: 100%"
                @change="handleOrderChange"
              >
                <el-option
                  v-for="item in orderOptions"
                  :key="item.outsourcingDocId || item.baseOutsourcingDocId"
                  :label="formatOrderLabel(item)"
                  :value="item.outsourcingDocId || item.baseOutsourcingDocId"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="预计到货日期">
              <el-date-picker
                v-model="form.baseExpectedArrivalDate"
                type="date"
                value-format="yyyy-MM-dd"
                format="yyyy/MM/dd"
                placeholder="请选择预计到货日期"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="出货单号">
              <el-input
                v-model="form.outsourcingtowarehouseNumber"
                placeholder="请输入出货单号"
                clearable
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="物流公司">
              <el-select
                v-model="form.baseLogisticsCompany"
                filterable
                allow-create
                default-first-option
                placeholder="请选择或输入物流公司"
                style="width: 100%"
              >
                <el-option
                  v-for="item in logisticsCompanyOptions"
                  :key="item"
                  :label="item"
                  :value="item"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="物流单号">
              <el-input
                v-model="form.baseLogisticsNumber"
                placeholder="请输入物流单号"
                clearable
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="联系方式">
              <el-input
                v-model="form.baseLogisticsContact"
                placeholder="请输入联系方式"
                clearable
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="备注">
              <el-input
                v-model="form.remark"
                type="textarea"
                :rows="2"
                placeholder="请输入备注"
                maxlength="512"
                show-word-limit
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </div>

    <div class="delivery-card delivery-card--detail">
      <div class="delivery-card__head delivery-card__head--plain">
        <span class="delivery-card__title">出货明细</span>
      </div>

      <el-table
        v-if="detailList.length"
        :data="detailList"
        border
        size="small"
        class="detail-table"
        :row-key="getDetailRowKey"
      >
        <el-table-column
          type="index"
          label="序号"
          width="55"
          align="center"
        />
        <el-table-column label="产品编号" min-width="120" show-overflow-tooltip>
          <template slot-scope="{ row }">
            {{ row.baseMaterialNumber || '--' }}
          </template>
        </el-table-column>
        <el-table-column label="产品名称" min-width="140" show-overflow-tooltip>
          <template slot-scope="{ row }">
            {{ row.baseMaterialName || '--' }}
          </template>
        </el-table-column>
        <el-table-column label="规格" min-width="140" show-overflow-tooltip>
          <template slot-scope="{ row }">
            {{ formatSpecLabel(row) }}
          </template>
        </el-table-column>
        <el-table-column label="批次号" min-width="120">
          <template slot-scope="{ row }">
            <el-input
              v-model="row.batchNumber"
              placeholder="请输入批次号"
              clearable
              size="small"
            />
          </template>
        </el-table-column>
        <el-table-column label="可发数量" min-width="100" align="left">
          <template slot-scope="{ row }">
            {{ formatQty(row.valueUnfinishedQuantity) }}
          </template>
        </el-table-column>
        <el-table-column label="本次出货" min-width="120" align="left">
          <template slot-scope="{ row }">
            {{ formatQty(row.baseOutsourcingFinishedQuantity) }}
          </template>
        </el-table-column>
        <el-table-column label="良品数量" min-width="120">
          <template slot-scope="{ row }">
            <el-input-number
              v-model="row.valueQualifiedQuantity"
              :min="0"
              :controls="false"
              size="small"
              class="detail-qty-input"
              @change="() => handlePartsQtyChange(row)"
              @input="() => handlePartsQtyChange(row)"
            />
          </template>
        </el-table-column>
        <el-table-column label="损耗数量" min-width="120">
          <template slot-scope="{ row }">
            <el-input-number
              v-model="row.valueUnqualifiedQuantity"
              :min="0"
              :controls="false"
              size="small"
              class="detail-qty-input"
              @change="() => handlePartsQtyChange(row)"
              @input="() => handlePartsQtyChange(row)"
            />
          </template>
        </el-table-column>
        <el-table-column label="单位" min-width="70" align="left">
          <template slot-scope="{ row }">
            {{ row.baseMaterialUnitName || '--' }}
          </template>
        </el-table-column>
      </el-table>

      <div v-else class="detail-empty">
        请先选择委外订单，系统将自动带出可出货明细
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable camelcase */
import {
  outsourcingDoc_vagueList,
  outsourcingtowarehouse_add,
  outsourcingtowarehouse_update,
  outsourcingtowarehouse_saveDraft,
  outsourcingtowarehouse_submitDraft,
  outsourcingtowarehouse_auditByGlobal
} from '@/api/myController/outsourcing'
import {
  LOGISTICS_COMPANIES,
  createDefaultForm,
  hasDocId,
  COOPERATIVE_DOC_STATUS,
  getCooperativeStatusLabel,
  formatOrderLabel,
  formatProductLabel,
  formatSpecLabel,
  formatQty,
  normalizeDetailRow,
  applyHeaderFields,
  findOrderRow,
  mergeOrderIntoDetailRow,
  pickOrderFormFields,
  pickOrderDetailSource,
  resolveSysDocRelation,
  validateDeliveryForm,
  buildAddPayload,
  buildSubmitPayload,
  buildDraftActionPayload,
  buildAuditPayload,
  resolveAuditGlobalId,
  syncFinishedQtyFromParts
} from '@/utils/outsourcingFinishDelivery'

export default {
  name: 'OutsourcingProductFinishAdd',
  props: {
    routeType: {
      type: [Number, String],
      default: 0
    },
    routeQuery: {
      default: () => ({})
    },
    finish_type: {
      type: Number,
      default: 0
    },
    itemList: {
      type: Array,
      default() {
        return []
      }
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      pageLoading: false,
      draftLoading: false,
      submitLoading: false,
      auditLoading: false,
      orderOptions: [],
      detailList: [],
      sysDocRelationDto: null,
      logisticsCompanyOptions: LOGISTICS_COMPANIES,
      form: createDefaultForm()
    }
  },
  computed: {
    docIdExists() {
      return hasDocId(this.form.outsourcingtowarehouseId)
    },
    isCooperativeDraft() {
      return Number(this.form.statusCooperativeDoc) === 0
    },
    showDraftButton() {
      return !this.docIdExists || this.isCooperativeDraft
    },
    submitButtonLabel() {
      if (this.docIdExists && !this.isCooperativeDraft) {
        return '保存'
      }
      return '提交'
    },
    showAuditButton() {
      return (
        this.docIdExists &&
        Number(this.form.statusCooperativeDoc) === COOPERATIVE_DOC_STATUS.NEW
      )
    },
    isAddMode() {
      return this.finish_type === 0
    },
    pageTitle() {
      if (this.readOnly) {
        return '委外出货单详情'
      }
      return this.finish_type === 1 ? '编辑委外出货单' : '新增委外出货单'
    },
    cardTitle() {
      if (this.readOnly) {
        return this.form.outsourcingtowarehouseNumber || '委外出货单'
      }
      return this.finish_type === 1 ? '编辑委外出货单' : '新建委外出货单'
    },
    statusLabel() {
      return getCooperativeStatusLabel(this.form.statusCooperativeDoc)
    },
    payloadContext() {
      return {
        form: this.form,
        detailList: this.detailList,
        sysDocRelationDto: this.sysDocRelationDto
      }
    }
  },
  created() {
    this.initPage()
  },
  methods: {
    formatOrderLabel,
    formatProductLabel,
    formatSpecLabel,
    formatQty,
    getDetailRowKey(row) {
      return row._rowKey
    },
    handlePartsQtyChange(row) {
      syncFinishedQtyFromParts(row)
    },
    async initPage() {
      this.pageLoading = true
      try {
        await this.loadOrderOptions()
        if (this.finish_type === 1 && this.itemList.length) {
          this.applyDocData(this.itemList[0], this.itemList)
          return
        }
        if (Number(this.routeType) === 1 && this.routeQuery) {
          this.fillFromOrder(this.routeQuery)
        }
      } finally {
        this.pageLoading = false
      }
    },
    applyDocData(header, details) {
      this.form = applyHeaderFields(this.form, header)
      const order = findOrderRow(
        this.orderOptions,
        this.form.baseOutsourcingDocId
      )
      const list = (details && details.length ? details : [header]).map(
        (item, index) => {
          let row = normalizeDetailRow(item, index)
          if (order) {
            row = mergeOrderIntoDetailRow(row, order)
          }
          return row
        }
      )
      this.detailList = list
      this.sysDocRelationDto = resolveSysDocRelation(header)
      this.ensureOrderOption({
        baseOutsourcingDocId: this.form.baseOutsourcingDocId,
        baseOutsourcingDocNumber: this.form.baseOutsourcingDocNumber,
        baseMaterialName: list[0]?.baseMaterialName
      })
    },
    fillFromOrder(orderRow) {
      if (!orderRow) {
        return
      }
      this.form = applyHeaderFields(this.form, pickOrderFormFields(orderRow))
      this.detailList = [
        normalizeDetailRow(pickOrderDetailSource(orderRow), 0)
      ]
      this.sysDocRelationDto = resolveSysDocRelation(orderRow)
    },
    ensureOrderOption(option) {
      const docId = option.baseOutsourcingDocId || option.outsourcingDocId
      if (
        !docId ||
        this.orderOptions.some(
          (item) =>
            (item.outsourcingDocId || item.baseOutsourcingDocId) === docId
        )
      ) {
        return
      }
      this.orderOptions.unshift({
        ...option,
        outsourcingDocId: docId,
        baseOutsourcingDocId: docId
      })
    },
    async loadOrderOptions() {
      try {
        const res = await outsourcingDoc_vagueList({
          orderParam: [{ asc: false, fieldName: 'createTime' }],
          outsourcingDocFilter: {},
          pageParam: { pageNum: 1, pageSize: 500 }
        })
        this.orderOptions = res.data.data?.dataList || []
      } catch (error) {
        this.orderOptions = []
      }
    },
    handleOrderChange(baseOutsourcingDocId) {
      if (!baseOutsourcingDocId) {
        this.detailList = []
        this.form.baseOutsourcingDocNumber = ''
        this.sysDocRelationDto = null
        return
      }
      const order = this.orderOptions.find(
        (item) =>
          (item.outsourcingDocId || item.baseOutsourcingDocId) ===
          baseOutsourcingDocId
      )
      if (order) {
        this.fillFromOrder(order)
      }
    },
    showValidationError(options) {
      const result = validateDeliveryForm({
        form: this.form,
        detailList: this.detailList,
        ...options
      })
      if (!result.valid) {
        this.$message.error(result.message)
      }
      return result.valid
    },
    async handleSaveDraft() {
      if (
        !this.showValidationError({ requireQty: false, allowEmptyDetails: true })
      ) {
        return
      }
      if (this.detailList.length && !this.showValidationError({ requireQty: false })) {
        return
      }
      this.draftLoading = true
      try {
        if (!this.docIdExists) {
          const addPayload = buildAddPayload(this.payloadContext)
          const addRes = await outsourcingtowarehouse_add(addPayload)
          this.form.outsourcingtowarehouseId = addRes.data.data
          await outsourcingtowarehouse_saveDraft(
            buildDraftActionPayload(this.form.outsourcingtowarehouseId)
          )
        } else if (this.isCooperativeDraft) {
          await outsourcingtowarehouse_update(
            buildSubmitPayload(this.payloadContext)
          )
        }
        this.$message.success('草稿已保存')
      } finally {
        this.draftLoading = false
      }
    },
    async handleSubmit() {
      if (!this.showValidationError({ requireQty: true })) {
        return
      }
      this.submitLoading = true
      try {
        let docId = this.form.outsourcingtowarehouseId
        if (!this.docIdExists) {
          const payload = buildAddPayload(this.payloadContext)
          const res = await outsourcingtowarehouse_add(payload)
          docId = res.data.data
          this.form.outsourcingtowarehouseId = docId
        } else if (this.isCooperativeDraft) {
          await outsourcingtowarehouse_update(
            buildSubmitPayload(this.payloadContext)
          )
          await outsourcingtowarehouse_submitDraft(
            buildDraftActionPayload(docId)
          )
        } else {
          await outsourcingtowarehouse_update(
            buildSubmitPayload(this.payloadContext)
          )
        }
        this.$message.success('操作成功')
        this.emitSubmitSuccess(docId)
      } finally {
        this.submitLoading = false
      }
    },
    emitSubmitSuccess(docId) {
      if (this.finish_type === 1) {
        this.$emit('backOrderState')
        return
      }
      this.$emit('backHome', 50, { outsourcingtowarehouseId: docId })
    },
    async handleAudit(type) {
      const auditType = Number(type) === 2 ? 2 : 1
      const globalId = resolveAuditGlobalId(this.form)
      if (!globalId) {
        this.$message.error('缺少单据 globalId')
        return
      }
      if (auditType === 1 && !this.showValidationError({ requireQty: true })) {
        return
      }
      this.auditLoading = true
      try {
        if (auditType === 1) {
          await outsourcingtowarehouse_update(
            buildSubmitPayload(this.payloadContext)
          )
        }
        await outsourcingtowarehouse_auditByGlobal(
          buildAuditPayload(globalId, auditType)
        )
        this.$message.success(auditType === 1 ? '审核成功' : '已取消审核')
        this.emitSubmitSuccess(this.form.outsourcingtowarehouseId)
      } finally {
        this.auditLoading = false
      }
    },
    backHome() {
      this.$emit('backHome')
    }
  }
}
</script>

<style scoped lang="scss">
.delivery-page {
  min-height: calc(100vh - 132px);
  background: #fff;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 4px;
}

.page-header__title {
  margin: 0 0 6px;
  font-size: 22px;
  line-height: 1.3;
  color: #1476c3;
  font-weight: 600;
}

.page-header__actions {
  display: flex;
  flex-shrink: 0;
  padding-top: 4px;
}

.delivery-card {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  margin-bottom: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.delivery-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  padding: 12px 20px;
  background: linear-gradient(90deg, #eef6ff 0%, #f9fcff 100%);
  border-bottom: 1px solid #e4e7ed;
}

.delivery-card__head--plain {
  background: #fff;
  border-bottom: 1px solid #ebeef5;
}

.delivery-card__head-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.delivery-card__label {
  color: #606266;
  font-size: 14px;
}

.delivery-card__title {
  color: #303133;
  font-size: 15px;
  font-weight: 600;
}

.status-tag {
  display: inline-block;
  padding: 3px 10px;
  font-size: 12px;
  line-height: 1.5;
  color: #e6a23c;
  background: #fdf6ec;
  border: 1px solid #f5dab1;
  border-radius: 4px;
  white-space: nowrap;
}

.delivery-form {
  padding: 20px 20px 4px;
}

.delivery-card--detail {
  padding-bottom: 16px;
}

.detail-table {
  margin: 16px 20px 0;
  width: calc(100% - 40px);
}

.detail-empty {
  margin: 16px 20px 0;
  padding: 28px 16px;
  color: #909399;
  font-size: 13px;
  line-height: 1.6;
  background: #fafbfc;
  border: 1px dashed #e4e7ed;
  border-radius: 4px;
}

::v-deep .delivery-form .el-form-item__label {
  padding-bottom: 0;
  line-height: 20px;
  color: #606266;
  font-size: 13px;
}

::v-deep .delivery-form .el-form-item.is-required:not(.is-no-asterisk) > .el-form-item__label:before {
  color: #f56c6c;
}

::v-deep .delivery-form .el-form-item {
  margin-bottom: 18px;
}

::v-deep .delivery-form .el-input__inner {
  height: 34px;
  line-height: 34px;
}

::v-deep .detail-table {
  .el-table__header th {
    background: #fafbfc;
    color: #606266;
    font-weight: 500;
  }

  .el-table__body td {
    vertical-align: middle;
  }
}

::v-deep .detail-qty-input {
  width: 100%;

  .el-input__inner {
    text-align: left;
    height: 32px;
    line-height: 32px;
  }
}
</style>
